import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import ContentLoader from "react-content-loader";
import { IImages } from "@/Views/Virtual/PhoneHomePageHorisontalNew";

import styles from "./PhotoItemNew.module.scss";
import { useMe } from "@/Hooks";
import { useSelectedScreens } from "@/entities/selectedScreens";
import { SIMILAR_SCREEN_STORAGE_KEY } from "../SimilarScreen/SimilarScreenModal/SimilarScreenModal";

interface PhotoItemProps {
  isPaywallOnly?: boolean;
  setAppVideoScreens;
  image: IImages; // AppXScreen object
  idVideo: number;
  handleClickPhone: (
    timestamp: number,
    videoId?: number,
    blank?: boolean,
  ) => void;
  handleImageLoad: () => void;
  onInView: (imageId: string, inView: boolean) => void;
  isActiveBookmark: boolean;
  apiDataVideosScreens: any;
  setAppReplays: (appReplays: any) => void;
  isLastItem: boolean;
  spinnerRef: React.RefObject<HTMLDivElement>;
  currentPage: number;
  handleOpenBillingPage: () => void;
  handleCloseBillingPage: () => void;
  onContextMenu: (
    event: React.MouseEvent,
    imageId: string,
    timestamp: number,
    imageUrl: string,
    position: { x: number; y: number },
  ) => void;
  ScreenNotBlurredID?: number;
  //imageAlt?: string;
  appData?: {
    icon: string;
    name: string;
    shortname: string;
  };
}

const ExploreReplayColumn = ({
  isPaywallOnly,
  setAppVideoScreens,
  image,
  idVideo,
  handleClickPhone,
  handleImageLoad,
  onInView,
  isActiveBookmark,
  apiDataVideosScreens,
  setAppReplays,
  isLastItem,
  spinnerRef,
  currentPage,
  handleOpenBillingPage,
  onContextMenu,
  ScreenNotBlurredID,
  //imageAlt,
  appData,
}: PhotoItemProps): JSX.Element => {
  const [selectedImage, setSelectedImage] = React.useState<string | undefined>(
    undefined,
  );

  const [selectedImageId, setSelectedImageId] = React.useState<null | number>(
    null,
  );
  const [imageIndex, setImageIndex] = React.useState<number | null>(null);
  const [isHover, setIsHover] = useState(false);
  const [isActiveSave, setIsActiveSave] = useState(false);

  const closeImageModal = () => {
    setSelectedImage(undefined);
    setImageIndex(null);
  };

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isLoading && apiDataVideosScreens) {
      setIsLoading(false);
      if (imageIndex !== null) {
        const nextIndex = imageIndex + 1;
        if (nextIndex < apiDataVideosScreens.length) {
          const nextImage = apiDataVideosScreens[nextIndex];
          setSelectedImage(nextImage.screen);
          setSelectedImageId(nextImage.id);
          setImageIndex(nextIndex);
        }
      }
    }
  }, [apiDataVideosScreens, currentPage]);

  const [currentModalImage, setCurrentModalImage] = useState<IImages | null>(
    null,
  );
  const [modalImageSelected, setModalImageSelected] = useState(false);

  const {
    addScreen,
    removeScreen,
    isSelected: checkIsSelected,
    selectedScreens,
  } = useSelectedScreens();

  const [isSelected, setIsSelected] = useState(false);

  // Add this function to handle selection in the modal
  const handleModalSelectImage = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!currentModalImage) return;

    if (checkIsSelected(currentModalImage.id)) {
      removeScreen(currentModalImage.id);
      setModalImageSelected(false);
      // If the modal image is the same as the main image, update main image selection state too
      if (currentModalImage.id === image.id) {
        setIsSelected(false);
      }
    } else {
      addScreen({
        id: currentModalImage.id,
        ts: currentModalImage.timestamp,
        app_video: currentModalImage.app_video,
        isSaved: currentModalImage.is_saved,
        is_saved_pk: currentModalImage.is_saved_pk,
      });
      setModalImageSelected(true);
      // If the modal image is the same as the main image, update main image selection state too
      if (currentModalImage.id === image.id) {
        setIsSelected(true);
      }
    }
  };

  // Handle selection for the main image view
  const handleSelectImage = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (checkIsSelected(image.id)) {
      removeScreen(image.id);
      setIsSelected(false);
      // If the modal is open and showing this image, update modal selection state too
      if (currentModalImage && currentModalImage.id === image.id) {
        setModalImageSelected(false);
      }
    } else {
      addScreen({
        id: image.id,
        ts: image.timestamp,
        app_video: image.app_video,
        isSaved: image.is_saved,
        is_saved_pk: image.is_saved_pk,
      });
      setIsSelected(true);
      // If the modal is open and showing this image, update modal selection state too
      if (currentModalImage && currentModalImage.id === image.id) {
        setModalImageSelected(true);
      }
    }
  };

  // Add this useEffect to update both selection states when selectedScreens changes
  useEffect(() => {
    // Update main image selection state
    setIsSelected(checkIsSelected(image.id));

    // Update modal image selection state if modal is open
    if (currentModalImage) {
      setModalImageSelected(checkIsSelected(currentModalImage.id));
    }
  }, [selectedScreens, image.id, currentModalImage, checkIsSelected]);

  //const [imageLoaded, setImageLoaded] = useState(false);
  const imageLoaded = true;
  const { ref: refIntersection, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
    rootMargin: "0px 100px 0px 0px",
  });

  const navigate = useNavigate();
  const { me } = useMe();

  useEffect(() => {
    onInView(image.id, inView);
  }, []);

  const handleExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedImage(image.screen);
    setSelectedImageId(image.id);
    setImageIndex(
      apiDataVideosScreens.findIndex((item) => item.id === image.id) ?? 0,
    );
    setCurrentModalImage(image);
    // Check if the modal image is selected when opening
    setModalImageSelected(checkIsSelected(image.id));
  };

  const handleContextMenu = (
    event: React.MouseEvent,
    imageId: string,
    timestamp: number,
    imageUrl: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    // Calculate position relative to the viewport
    const x = event.clientX + window.scrollX;
    const y = event.clientY + window.scrollY;

    onContextMenu(event, imageId, timestamp, imageUrl, { x, y });
  };

  const handleNextImage = () => {
    if (imageIndex !== null && apiDataVideosScreens) {
      const nextIndex = imageIndex + 1;

      if (nextIndex >= apiDataVideosScreens.length) {
        const nextImage = apiDataVideosScreens[0];
        setImageIndex(0);
        setSelectedImage(nextImage.screen);
        setSelectedImageId(nextImage.id);
        setCurrentModalImage(nextImage);
        // Check if the next image is selected
        setModalImageSelected(checkIsSelected(nextImage.id));
        return;
      }

      const nextImage = apiDataVideosScreens[nextIndex];
      setSelectedImage(nextImage.screen);
      setSelectedImageId(nextImage.id);
      setImageIndex(nextIndex);
      setCurrentModalImage(nextImage);
      // Check if the next image is selected
      setModalImageSelected(checkIsSelected(nextImage.id));
    }
  };

  const handlePrevImage = () => {
    if (imageIndex !== null && apiDataVideosScreens) {
      const prevIndex =
        (imageIndex - 1 + apiDataVideosScreens.length) %
        apiDataVideosScreens.length;
      const prevImage = apiDataVideosScreens[prevIndex];
      setSelectedImage(prevImage.screen);
      setSelectedImageId(prevImage.id);
      setImageIndex(prevIndex);
      setCurrentModalImage(prevImage);
      // Check if the previous image is selected
      setModalImageSelected(checkIsSelected(prevImage.id));
    }
  };

  useEffect(() => {
    // todo: red: its wrong to handle these events here insdie each photo image
    const handleKeyDown = (event: KeyboardEvent) => {
      if (selectedImage) {
        if (event.key === "ArrowRight") {
          handleNextImage();
        } else if (event.key === "ArrowLeft") {
          handlePrevImage();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage, handleNextImage, handlePrevImage]);

  // Add this function to handle the Similar button click
  const handleSimilarClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    const screenToSet = currentModalImage || image;

    const similarScreenData = {
      id: screenToSet.id,
      screen: screenToSet.screen,
      appId: screenToSet.app_video || idVideo,
      timestamp: screenToSet.timestamp,
      appIcon: appData?.icon || "",
      appName: appData?.name || "",
      appDescription: appData?.shortname || "",
    };

    localStorage.setItem(
      SIMILAR_SCREEN_STORAGE_KEY,
      JSON.stringify(similarScreenData),
    );

    // Close the modal if it's open
    if (selectedImage) {
      closeImageModal();
    }

    window.open(`/similar-screens/${screenToSet.id}`, "_blank");
  };

  const imageAlt = image.vtags ? image.vtags.join(", ") : "";

  return (
    <>
      {/* {true && (
          <div className={`${image.label === "paywall" ? styles.spanLabelPaywall : styles.spanLabel}`}>
            <p className={styles.leftLine} />
            <span className={styles.stickySpan}>{item.label}</span>
            <p className={styles.rightLine} />
          </div>
        )} */}
      <div
        ref={refIntersection}
        data-image-id={image.id}
        style={{ pointerEvents: "auto" }}
        onMouseEnter={() => setIsActiveSave(true)}
        onMouseLeave={() => setIsActiveSave(false)}
        onContextMenu={(e) =>
          handleContextMenu(e, image.id, Number(image.timestamp), image.screen)
        }
      >
        <div
          className={image.is_blurry ? styles.screenItemContainerNotLogged : ""}
        >
          <div
            className={image.is_blurry ? styles.screenItemNotLogged : ""}
            onClick={handleExpandClick}
          >
            {/* {!isActiveBookmark && ( */}
            {image.is_blurry && (
              <div className={styles.screenItemContentNotLogged}>
                <div className={styles.titleNotLogged}>
                  <p className={styles.titleNotLoggedPro}>
                    <span>Pro</span>
                  </p>
                  <p className={styles.titleNotLoggedMember}>Member's Only</p>
                </div>
                <button
                  onClick={handleOpenBillingPage}
                  className={styles.buttonNotLogged}
                >
                  <span>Unlock Pro</span>
                </button>
              </div>
            )}
            <div
              className={`${styles.screenItem} ${
                isPaywallOnly && image.label !== "paywall"
                  ? styles.paywallDimmed
                  : ""
              } ${
                ScreenNotBlurredID !== undefined &&
                ScreenNotBlurredID !== image.id
                  ? styles.paywallDimmed
                  : ""
              }`}
              tabIndex={0}
            >
              <img
                src={image.screen}
                alt={imageAlt}
                className={styles.img}
                //   onLoad={() => {
                //     setImageLoaded(true);
                //     handleImageLoad();
                //   }}
                style={{ display: imageLoaded ? "block" : "none" }}
                loading="eager"
              />
              {!imageLoaded && (
                <ContentLoader
                  speed={2}
                  width={254}
                  height={581.4}
                  viewBox="0 0 254 581.4"
                  backgroundColor="#f3f3f3"
                  foregroundColor="#ecebeb"
                >
                  <rect x="0" y="0" rx="7" ry="7" width="254" height="581.4" />
                </ContentLoader>
              )}
              {(isActiveSave || isSelected) && (
                <div className={styles.selectButtonContainer}>
                  <div className={styles.selectButtonContainerInner}>
                    {isActiveSave && image.is_saved && (
                      <div className={styles.savedTag}>
                        <p>Saved</p>
                      </div>
                    )}

                    <button
                      className={`${styles.selectButton} ${isSelected ? styles.selected : ""}`}
                      onClick={handleSelectImage}
                    >
                      {isSelected && (
                        <img src="/img/blackIcon/Check.svg" alt="selectImage" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {isActiveSave && (
                <div className={styles.bottomMenuContainer}>
                  <div className={styles.bottomMenuContainerInner}>
                    <button onClick={handleSimilarClick}>
                      <img src="/img/similar-icon.svg" alt="similar" />
                      <p>Similar</p>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClickPhone(
                          Number(image.timestamp),
                          image.app_video,
                        );
                      }}
                    >
                      <img src="/img/see-video-icon.svg" alt="see-video" />
                      <p>See Video</p>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <Modal
          isOpen={!!selectedImage}
          onRequestClose={closeImageModal}
          contentLabel="Image Modal"
          className={styles.modal}
          overlayClassName={styles.overlay}
        >
          <div
            className={styles.modalContent}
            onContextMenu={(e) =>
              handleContextMenu(
                e,
                image.id,
                Number(
                  currentModalImage
                    ? currentModalImage.timestamp
                    : image.timestamp,
                ),
                image.screen,
              )
            }
          >
            <div
              className={styles.containerImage}
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => setIsHover(false)}
              onClick={() =>
                handleClickPhone(
                  Number(
                    currentModalImage
                      ? currentModalImage.timestamp
                      : image.timestamp,
                  ),
                  image.app_video,
                )
              }
            >
              {isLoading ? (
                <ContentLoader
                  speed={2}
                  width={400}
                  height={800}
                  viewBox="0 0 400 800"
                  backgroundColor="#f3f3f3"
                  foregroundColor="#ecebeb"
                >
                  <rect x="0" y="0" rx="2" ry="2" width="400" height="800" />
                </ContentLoader>
              ) : (
                <img
                  className={styles.modalContentPhoto}
                  src={selectedImage!}
                  alt="Enlarged pic"
                  loading="lazy"
                />
              )}
              {(isHover || modalImageSelected) && (
                <div className={styles.modalSelectButtonContainer}>
                  <div className={styles.modalSelectButtonContainerInner}>
                    {isHover &&
                      currentModalImage &&
                      currentModalImage.is_saved && (
                        <div className={styles.savedTag}>
                          <p>Saved</p>
                        </div>
                      )}

                    <button
                      className={`${styles.selectButton} ${modalImageSelected ? styles.selected : ""}`}
                      onClick={handleModalSelectImage}
                    >
                      {modalImageSelected && (
                        <img src="/img/blackIcon/Check.svg" alt="selectImage" />
                      )}
                    </button>
                  </div>
                </div>
              )}
              {isHover && (
                <div className={styles.modalBottomMenuContainer}>
                  <div className={styles.modalBottomMenuContainerInner}>
                    <button onClick={handleSimilarClick}>
                      <img src="/img/similar-icon.svg" alt="similar" />
                      <p>Similar</p>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClickPhone(
                          Number(
                            currentModalImage
                              ? currentModalImage.timestamp
                              : image.timestamp,
                          ),
                          currentModalImage?.app_video || image.app_video,
                        );
                      }}
                    >
                      <img src="/img/see-video-icon.svg" alt="see-video" />
                      <p>See Video</p>
                    </button>
                  </div>
                </div>
              )}
            </div>
            <img
              className={styles.arrowRight}
              src="/img/whiteIcon/ChevronRight.svg"
              alt="arrowRightImg"
              onClick={handleNextImage}
              loading="lazy"
              style={{
                opacity:
                  imageIndex === apiDataVideosScreens.length - 1 ? 0.5 : 1,
              }}
            />
            <img
              className={styles.arrowLeft}
              src="/img/whiteIcon/ChevronLeft.svg"
              alt="arrowLeftImg"
              onClick={handlePrevImage}
              loading="lazy"
            />
          </div>
        </Modal>
      </div>
    </>
  );
};

export default ExploreReplayColumn;
